import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Form,
  InputNumber,
  Button,
  Table,
  message,
  Alert,
  Tag,
  Space,
  Progress,
  Tooltip
} from 'antd';
import {
  PercentageOutlined,
  DollarOutlined,
  UserOutlined,
  RiseOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { api } from '../utils/api';

const MarginSettings = () => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [utilization, setUtilization] = useState(null);
  const [utilizationLoading, setUtilizationLoading] = useState(false);
  const [form] = Form.useForm();
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchMarginSettings();
    fetchMarginUtilization();
  }, []);

  const fetchMarginSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/orders/admin/margin-settings');
      
      setSettings(response.data);
      form.setFieldsValue({ marginPercentage: response.data.marginPercentage });
    } catch (error) {
      message.error('Failed to fetch margin settings');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMarginUtilization = async () => {
    try {
      setUtilizationLoading(true);
      const response = await api.get('/api/orders/admin/margin-utilization');
      
      setUtilization(response.data);
    } catch (error) {
      message.error('Failed to fetch margin utilization');
      console.error('Error:', error);
    } finally {
      setUtilizationLoading(false);
    }
  };

  const updateMarginSettings = async (values) => {
    try {
      setLoading(true);
      const response = await api.post('/api/orders/admin/margin-settings', values);
      
      message.success(response.data.message);
      setSettings(response.data.settings);
      fetchMarginUtilization(); // Refresh utilization data
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update margin settings');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStatisticsCards = () => {
    const stats = [
      {
        title: 'Margin Percentage',
        value: settings?.marginPercentage || 50,
        suffix: '%',
        icon: 'PercentageOutlined',
        color: '#52c41a'
      },
      {
        title: 'Total Users',
        value: utilization?.totalUsers || 0,
        icon: 'UserOutlined',
        color: '#1890ff'
      },
      {
        title: 'Total Margin Used',
        value: utilization?.totalMarginUsed || 0,
        prefix: '₹',
        icon: 'DollarOutlined',
        color: '#faad14'
      },
      {
        title: 'Total Position Value',
        value: utilization?.totalPositionValue || 0,
        prefix: '₹',
        icon: 'RiseOutlined',
        color: '#722ed1'
      }
    ];

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {stats.map((stat, index) => (
          <Col xs={12} sm={12} md={6} lg={6} key={index}>
            <Card
              style={{
                background: `linear-gradient(135deg, ${stat.color}15, ${stat.color}08)`,
                border: `1px solid ${stat.color}30`,
                borderRadius: isMobile ? 12 : 16,
                height: isMobile ? 100 : 120,
                position: 'relative'
              }}
            >
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={stat.prefix}
                suffix={stat.suffix}
                valueStyle={{
                  color: stat.color,
                  fontSize: isMobile ? 18 : 24,
                  fontWeight: 'bold'
                }}
                titleStyle={{
                  fontSize: isMobile ? 11 : 14,
                  fontWeight: 600
                }}
              />
              <div style={{
                position: 'absolute',
                top: 16,
                right: 16,
                fontSize: isMobile ? 20 : 24,
                color: `${stat.color}40`,
                opacity: 0.6
              }}>
                {stat.icon === 'PercentageOutlined' && <PercentageOutlined />}
                {stat.icon === 'UserOutlined' && <UserOutlined />}
                {stat.icon === 'DollarOutlined' && <DollarOutlined />}
                {stat.icon === 'RiseOutlined' && <RiseOutlined />}
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const utilizationColumns = [
    {
      title: '👤 User',
      key: 'user',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.user.name}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.user.email}</div>
        </div>
      )
    },
    {
      title: '💰 Margin Used',
      dataIndex: 'totalMarginUsed',
      key: 'totalMarginUsed',
      render: (value) => `₹${value?.toLocaleString() || 0}`
    },
    {
      title: '📊 Position Value',
      dataIndex: 'totalPositionValue',
      key: 'totalPositionValue',
      render: (value) => `₹${value?.toLocaleString() || 0}`
    },
    {
      title: '🔢 Leverage',
      dataIndex: 'leverageRatio',
      key: 'leverageRatio',
      render: (value) => (
        <Tag color={value > 5 ? 'red' : value > 3 ? 'orange' : 'green'}>
          {value}x
        </Tag>
      )
    },
    {
      title: '📈 Orders',
      dataIndex: 'orders',
      key: 'orders',
      render: (orders) => orders?.length || 0
    }
  ];

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #722ed1, #9254de)',
        borderRadius: isMobile ? 16 : 20,
        padding: isMobile ? 20 : 32,
        marginBottom: 24,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 100,
          height: 100,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
          filter: 'blur(20px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 80,
          height: 80,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '50%',
          filter: 'blur(15px)'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? 22 : 32,
            fontWeight: 700,
            color: 'white'
          }}>
            💰 Margin Management
          </h1>
          <p style={{
            margin: '8px 0 0 0',
            fontSize: isMobile ? 14 : 16,
            opacity: 0.9,
            color: 'white'
          }}>
            Configure margin percentage for commodity trading and monitor user utilization
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      {renderStatisticsCards()}

      {/* Margin Settings Form */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              background: 'linear-gradient(135deg, #722ed1, #9254de)',
              borderRadius: 8,
              padding: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SettingOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600 }}>
              ⚙️ Commodity Margin Configuration
            </span>
          </div>
        }
        style={{ marginBottom: 24 }}
      >
        <Alert
          message="Margin Trading Information"
          description="Set the margin percentage for commodity trading. Users will only need to pay this percentage of the total trade value as margin. Example: 50% margin means ₹50,000 margin for a ₹1,00,000 trade."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={updateMarginSettings}
          initialValues={{ marginPercentage: 50 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="📊 Commodity Margin Percentage"
                name="marginPercentage"
                rules={[
                  { required: true, message: 'Please enter margin percentage' },
                  { type: 'number', min: 1, max: 100, message: 'Margin must be between 1% and 100%' }
                ]}
                tooltip="Percentage of trade value required as margin for commodity trades"
              >
                <InputNumber
                  min={1}
                  max={100}
                  suffix="%"
                  style={{ width: '100%', height: isMobile ? 48 : 40 }}
                  placeholder="Enter margin percentage"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                background: 'linear-gradient(135deg, #722ed1, #9254de)',
                border: 'none',
                borderRadius: 8,
                height: isMobile ? 48 : 40,
                fontWeight: 600,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              💾 Update Margin Settings
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Margin Utilization */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              background: 'linear-gradient(135deg, #1890ff, #40a9ff)',
              borderRadius: 8,
              padding: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UserOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600 }}>
              👥 User Margin Utilization
            </span>
          </div>
        }
      >
        {utilization?.users?.length > 0 ? (
          <Table
            dataSource={utilization.users}
            columns={utilizationColumns}
            loading={utilizationLoading}
            pagination={{
              pageSize: isMobile ? 8 : 10,
              showSizeChanger: !isMobile,
              showQuickJumper: !isMobile
            }}
            scroll={{ x: isMobile ? 800 : undefined }}
            size={isMobile ? 'small' : 'default'}
            rowKey={(record) => record.user.id}
          />
        ) : (
          <Alert
            message="No Margin Trading Activity"
            description="No users are currently using margin trading for commodities"
            type="info"
            showIcon
          />
        )}
      </Card>
    </div>
  );
};

export default MarginSettings;
